import React from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import Layout from "../components/layout"
import SEO from "../components/seo"

const Content = styled('div')`
  width: clamp(400px, 50vw, 960px);
  margin: 0 auto;
  margin-bottom: 240px;
  position:relative;
  top: 160px;
  
  & > *{
    text-align: left;
  }
  h1 {
   text-transform: uppercase;
  }
  h2 {
    font-weight: 700;
    margin-top: 24px;
    font-size: 26px;
    margin-bottom: 0;
  }
  h3 {
    margin-top: 24px;
    font-size: 20px;
  }
  & time {
    margin-bottom: 24px;
    font-size: 12px;
    display: block;
  }
  & span.link {
    display: block;
    margin-top: 48px;
  }
  hr {
  height: 1px;
  border: none;
  background-color: var(--sp-orange);
  margin-top: 24px;
  }
  li {
  line-height: 1.6;
  }
`

const ReleaseNotesPage = () => (
  <Layout navForced>
    <SEO title={'Sheetplanner - Release Notes'}/>
    <Content>
      <h1>Release Notes</h1>

      <h2>SheetPlanner 5.3 Release Notes</h2>
      <time>May 3rd, 2026</time>
      <p>
        SheetPlanner 5.3 is a big update to this spreadsheet-like project planning tool. Major enhancements in version 5.3 include:
      </p>
      <ul>
        <li>Added a new Table view that groups rows by a selected List or Symbol column, and supports filtering and drag-and-drop between groups.</li>
        <li>Added a built-in Parent column so you can quickly jump to a row’s parent or reassign it from a popover.</li>
        <li>Added Apple Intelligence-powered Generate Child Rows, which can create child rows from a note-based prompt and populate relevant columns as it generates.</li>
        <li>Expanded themes to all views on all platforms, with a new Sheet pane in the Views Inspector for managing them.</li>
        <li>Added a new Image theme with built-in image picking, file and Photos selection, Image Playground creation, and a readability control for clearer content over images.</li>
        <li>Added Pin Row, so important completed rows can stay visible and show a pinned indicator in the Outline.</li>
        <li>Improved editing behavior, including fixing Cmd-Up/Cmd-Down while editing on Mac and making Return insert a child row when a focused row is selected.</li>
        <li>Fixed several stability issues, including crashes in Board view, when closing windows with outline links visible, and when showing the Inspector from Outline on iPhone.</li>
        <li>Fixed several Mac document and window issues, including windows unexpectedly shrinking, recent documents reopening as locked, and some third-party cloud documents getting stuck in Preparing.</li>
        <li>Improved view consistency with Timeline row height updates, Board support for Smart Filter Matches & Children, the missing Outline pane on iPad and iPhone, and support for hiding menu icons with the macOS global setting.</li>
        <li>Plus many other improvements; see the full release notes on the SheetPlanner website for details.</li>
      </ul>

      <p>
        Read on for a detailed list of changes:
      </p>

      <h3>Table view</h3>
      <ul>
        <li>Added a new Table view that groups child rows by a selected List or Symbol column.</li>
        <li>Added a Table submenu in the View menu to choose which column to use.</li>
        <li>Added a Table pane in the Views Inspector as another way to choose which column to use.</li>
        <li>The chosen column is remembered for next time.</li>
        <li>The Outline and Table views are mutually exclusive, even when showing multiple views. [Mac]</li>
        <li>When the Table and Timeline views are shown together, the Timeline shows special dotted bars for the table groups. [Mac]</li>
        <li>A row can appear under multiple groups, e.g. if grouping on Tags and a row has multiple tags.</li>
        <li>Can drag rows between Table groups to change their value.</li>
        <li>Group rows can't be dragged, and don't show row numbers or menus.</li>
        <li>Adding a row by a selected row will inherit that same group.</li>
        <li>Filters are supported, grouping the filter results.</li>
      </ul>

      <h3>Parent column</h3>
      <ul>
        <li>Added a built-in Parent button column that can jump to or reassign a row's parent.</li>
        <li>Click or tap the displayed topic in a Parent cell to jump to that row.</li>
        <li>Select the Parent cell (clicking or tapping to the right of the topic) then click/tap again, press the Space bar, or Cmd-E to show a popover listing available rows; choose one to move the selected row to that new parent.</li>
      </ul>

      <h3>Apple Intelligence</h3>
      <ul>
        <li>Added a Generate Child Rows command in the Organize menu, the row menu, and the Add toolbar menu, if Apple Intelligence is available, on all platforms.</li>
        <li>It adds child rows into the outline as they are generated.</li>
        <li>Use the note to write a prompt to guide the rows to generate and columns to populate; the more detail you provide, the more likely to be what you want.</li>
        <li>If the selected row already has child rows, you'll be prompted to Replace, Append, or Cancel before rows are added.</li>
        <li>Generated rows always include Topic and can also fill other columns (for example Start Date, Finish Date, and People) when sensible; mention the column names in the note to increase the chances of populating them.</li>
      </ul>

      <h3>Themes for all views</h3>
      <ul>
        <li>Added a Sheet pane in the Views Inspector.</li>
        <li>Moved the Theme options from the Outline View Inspector to the new Sheet one. [Mac]</li>
        <li>Themes now work across all views, on all platforms.</li>
        <li>Themes continue to be local to your device, so each person can choose how they want each sheet to appear.</li>
      </ul>

      <h3>Image theme</h3>
      <ul>
        <li>Added an Image theme, with buttons to choose or remove an image.</li>
        <li>A Use Image button lets you quickly pick from bundled built-in photos.</li>
        <li>A Choose Image button lets you pick an image file from your disk.</li>
        <li>A Choose Photo button lets you can pick a photo from your Photos Library (the app won't have access to any other photos).</li>
        <li>An Image Playground button lets you create new images based on a description.</li>
        <li>Also includes a readability slider to reduce the saturation, add a neutral veil, and apply increasing blur to the image, to make the views easier to read on complex images.</li>
      </ul>

      <h3>Pin row</h3>
      <ul>
        <li>Added a Pin Row command in the Organize menu and row menu.</li>
        <li>Pinned rows stay visible even if they are completed and completed rows are hidden.</li>
        <li>When pinned, an icon appears in the Outline row number column.</li>
      </ul>

      <h3>Other changes</h3>
      <ul>
        <li>Fixed Cmd-Up and Cmd-Down moving the cell selection instead of the insertion point while editing. [Mac]</li>
        <li>Pressing Return with a focused row selected will now insert a child row instead of a sibling, to avoid confusing the focus.</li>
        <li>Fixed a rare crash with the Board view when creating a folder. [Mac]</li>
        <li>Fixed a crash that could happen when closing a document window with outline links visible. [Mac]</li>
        <li>Fixed a macOS issue where opening a document or changing views in single-view mode could shrink the document window. [Mac]</li>
        <li>Fixed a crash when showing the Inspector from Outline on iPhone.</li>
        <li>Fixed the Timeline row heights not updating when the Outline row heights change. [Mac]</li>
        <li>Removed a superfluous sync notification about not being saved on iCloud Drive.</li>
        <li>Now respects the defaults write -g NSMenuEnableActionImages -bool NO global setting; if used, icons are hidden from menus. [Mac]</li>
        <li>Added the previously missing Outline pane in the Views Inspector. [iPad & iPhone]</li>
        <li>Fixed recent documents sometimes reopening as locked on Mac until opened again from Finder. [Mac]</li>
        <li>Fixed some third-party cloud documents getting stuck in Preparing after live sync failed to enable. [Mac]</li>
        <li>When a Smart Filter uses the option to include Matches & Children, the Board view now includes eligible children.</li>
      </ul>
      <p>
        Note: changes apply to the Mac, iPad, and iPhone editions, unless otherwise noted.
      </p>

      <hr />

      <h2>SheetPlanner 5.2 Release Notes</h2>
      <time>March 11th, 2026</time>
      <p>
        SheetPlanner 5.2 is a big update to this spreadsheet-like project planning tool. Major enhancements in version 5.2 include:
      </p>
      <ul>
        <li>Major usability improvements across Outline, Timeline, Board, and Year views, including better editing behavior, safer multi-row pasting, improved column resizing on Mac, and multiple visual and interaction fixes.</li>
        <li>Significant Inspector improvements across platforms, including smoother updates, restored group states, improved layout behavior, and better integration with Outline editing.</li>
        <li>Sidebar enhancements on iPad and Mac, including inline sidebar display by default on iPad, improved folder handling and drag behavior, document title controls in the sidebar, and better keyboard navigation.</li>
        <li>Modernized toolbar and menu system with a cleaner unified Mac toolbar, adaptive toolbar controls on iPad and iPhone, improved menu organization, and additional keyboard shortcuts.</li>
        <li>Enhanced Smart Filters with a redesigned popover editor on Mac, support for naming and detaching the editor, improved focus handling, and better restoration of focus and filter state.</li>
        <li>Improved focus-based filtering, including hierarchical topic selection, automatic sidebar expansion to show focused rows, and better preservation of focus state when filters are cleared.</li>
        <li>Date picker enhancements in the Outline view, including support for parent rows with calculations, additional value options, and improved reliability.</li>
        <li>Expanded live sync support to cloud providers such as Dropbox, Google Drive, and OneDrive, with improved coordination between devices to avoid cloud-drive conflicts.</li>
        <li>Reliability improvements to live sync, including fixes for missed sync starts, and multiple stability improvements.</li>
        <li>Added sync diagnostics logging tools to help trace and debug syncing and publisher behavior.</li>
        <li>Major performance improvements throughout the app, reducing unnecessary reloads, layout work, logging overhead, and view refresh churn.</li>
        <li>Updated completed button styling with a new circular style, customizable colors, and consistent appearance across views.</li>
        <li>Added a new single-view mode option on Mac, allowing each tab to show either a single view or multiple views.</li>
        <li>Added a new About window on iPad and iPhone.</li>
        <li>Added new settings for space bar behavior in the Outline and default font and color for notes.</li>
        <li>Plus many other improvements; see the full release notes on the SheetPlanner website for details.</li>
      </ul>

      <p>
        Read on for a detailed list of changes:
      </p>

      <h3>Outline view fixes and improvements</h3>
      <ul>
        <li>Double-clicking Outline column dividers now sizes the column to fit the visible values. [Mac]</li>
        <li>Added a Resize to Fit Content command in the column menu, that does the same as the above.</li>
        <li>When a single cell is selected, pasting multiple rows will now insert extra rows after the selected one, rather than overwriting subsequent rows, to avoid the risk of data loss.</li>
        <li>Fixed Topic editing in Outline rows so the first line stays visible while multi-line row height expands. [Mac]</li>
        <li>Fixed an occasional beep when indenting, outdenting, or toggling edit mode while a cell is being edited. [Mac]</li>
        <li>Added support for starting Outline cell editing by typing on an external keyboard after selecting a cell. [iPad]</li>
        <li>Fixed live Topic row sizing while editing so row height expands as you type. [iPad & iPhone]</li>
        <li>Fixed a crash that could occur while editing a Topic when syncing updates triggered view reloads. [iPad & iPhone]</li>
        <li>When a row has a long note, it is now loaded and displayed inline correctly (before it was only loaded if shown in a new tab).</li>
        <li>Fixed iPad date picker selections in the Outline so choosing a date updates the cell value. [iPad & iPhone]</li>
        <li>Fixed note typing color in Dark Mode when starting to edit a note so new text uses the appearance-adapted note style color. [Mac]</li>
        <li>Fixed an occasional beep when ending editing the Topic cell.</li>
        <li>Fixed dragging columns. [Mac]</li>
      </ul>

      <h3>Timeline view fixes</h3>
      <ul>
        <li>Fixed occasional missing horizontal dividers.</li>
        <li>Fixed the shadows in the Timeline view when the Outline is also visible. [Mac]</li>
      </ul>

      <h3>Board view fixes</h3>
      <ul>
        <li>Fixed Board card dragging so drops use the actual drop location and no longer intermittently land in the wrong lane. [iPad & iPhone]</li>
        <li>Fixed Board card selection so selecting a card no longer recenters the board and highlight drawing updates without visual artifacts. [iPad & iPhone]</li>
        <li>Fixed Board scrolling artifacts where cards could briefly appear in the wrong position while moving around the board. [iPad & iPhone]</li>
        <li>Fixed live sync refresh so Board updates after remote changes are received.</li>
      </ul>

      <h3>Year view improvement</h3>
      <ul>
        <li>Now shows additional months to fill visible empty space.</li>
      </ul>

      <h3>Inspector fixes and improvements</h3>
      <ul>
        <li>The Inspector now uses a full-height background and a rounded control to switch pages.</li>
        <li>The Inspector now slides in from the right, instead of a popover, on iPadOS 26. [iPad]</li>
        <li>Updated the iPhone Inspector sheet to open at half height with drag support to full height or dismiss. [iPhone]</li>
        <li>Made the List Inspector item color pickers use a circular shape instead of appearing oval. [Mac]</li>
        <li>The iPad Inspector now uses an opaque background like on Mac. [iPad]</li>
        <li>Changed the Mac Inspector toolbar icon to the modern style. [Mac]</li>
        <li>The Inspector now also draws more smoothly when changing the layout, like toggling the filter bar. [Mac]</li>
        <li>The Inspector now remembers and restores the expanded/collapsed state of the groups (at last!). [Mac]</li>
        <li>Fixed crash when editing dates via date picker in the Inspector. [iPad & iPhone]</li>
        <li>Fixed editing the Topic in the Values Inspector. [iPad & iPhone]</li>
        <li>Changes in the Inspector are now live-updated in the Outline view. [iPad & iPhone]</li>
      </ul>

      <h3>Enhanced completed button style</h3>
      <ul>
        <li>Added a new Circle style for the completed button; set in the Value Inspector for the Topic cell.</li>
        <li>Added a color picker to change the color of the completed button.</li>
        <li>Unified drawing of the completed button (appears in the sidebar, Outline, Board, and Month views).</li>
        <li>Fixed checkmark state in the sidebar for partially complete rows. [Mac]</li>
        <li>Fixed sometimes not saving the completed button style (in the Format Inspector for the Topic column).</li>
      </ul>

      <h3>Sidebar improvements</h3>
      <ul>
        <li>The sidebar is now shown inline by default, so it's readily available for focusing and switching sheets. It can be hidden if not needed. [iPad]</li>
        <li>Added a menu command in the View menu and action (...) menu to toggle between showing the sidebar and Inspector inline or in an overlay (as before). [iPad]</li>
        <li>Fixed loading of a document with custom folders in the sidebar.</li>
        <li>Fixed dragging a sheet to a custom folder, and related dragging validation. [Mac]</li>
        <li>The button to toggle the sidebar now appears above the sidebar when visible, or before the title when hidden. [Mac]</li>
        <li>Fixed the sidebar being empty if the document opens with the sidebar hidden, then it is shown. [Mac]</li>
        <li>The split dividers are now hidden, for a cleaner look on macOS 26. [Mac]</li>
        <li>Fixed unnecessary space on the left edge. [iPad & iPhone]</li>
        <li>Added a document title at the top of the sidebar, which shows the filename, selected sheet, and edit state, like on Mac. [iPad & iPhone]</li>
        <li>Tap the title to show a menu, with information about the document at the top. [iPad & iPhone]</li>
        <li>The menu includes a Rename command, to rename the document. [iPad & iPhone]</li>
        <li>The menu also includes New Sheet, Duplicate Sheet, and Duplicate Sheet and Rows commands. [iPad & iPhone]</li>
      </ul>

      <h3>Toolbar and menus improvements</h3>
      <ul>
        <li>The window titlebar and toolbar now uses a unified appearance, with the document title and toolbar buttons on the same level. [Mac]</li>
        <li>The current sheet name and/or any filter/focus now appears below the document title. [Mac]</li>
        <li>The toolbar now defaults to only showing the icons, as is the modern convention (but you can re-show the title text if you wish). [Mac]</li>
        <li>The toolbar now appears fully-formed when opening a document, instead of briefly showing extra buttons. [Mac]</li>
        <li>Changed the Views control to use regular bar buttons, and automatically collapse to a single button with a menu if the window is narrow. [iPad & iPhone]</li>
        <li>Removed the Connect item from the toolbar.</li>
        <li>Added the Connect submenu to the Organize menu. [iPad]</li>
        <li>Added titles to the toolbar buttons; they appear in the overflow menu if the window is narrow. [iPad & iPhone]</li>
        <li>When displaying the Inspector inline, the Inspector toolbar button is hidden. [iPad]</li>
        <li>Added the Welcome to SheetPlanner, What's New, etc items to the Help menu in the menubar. [iPad]</li>
      </ul>

      <h3>Single view mode option</h3>
      <ul>
        <li>Added a new Show Single View / Show Multiple Views option in the View menu. [Mac]</li>
        <li>This option is tab-specific. [Mac]</li>
        <li>Holding the Option key when selecting a view via the toolbar will temporarily reverse this setting. [Mac]</li>
        <li>Changed the keyboard shortcut of Show Views Vertically/Horizontally to ⇧⌥⌘0, as the new view mode option uses ⌥⌘0. [Mac]</li>
        <li>The Show Views Vertically/Horizontally option is not available when using Single view mode, since it doesn't apply. [Mac]</li>
        <li>By default, documents use Single view mode, unless they already have multiple views displayed on upgrade. [Mac]</li>
        <li>The Project Plan templates default to Multiple view mode, to show the Outline and Timeline. [Mac]</li>
        <li>A tip popover appears when first using the views control, to inform about the view mode option. [Mac]</li>
      </ul>

      <h3>The Smart Filter editor is now in a popover</h3>
      <ul>
        <li>The filter editor now appears in a popover on Mac, like on iPad. [Mac]</li>
        <li>It now includes a Name field; leave blank to use an automatic name, or enter a custom one. [Mac]</li>
        <li>The popover can be dragged from any empty space above the criteria to detach it to a floating window. [Mac]</li>
        <li>The controls are larger than before, and laid out more tidily. [Mac]</li>
        <li>Moved the restore current view and focus options to the top.</li>
        <li>When opening a template with saved filters, the Filter Bar is shown by default.</li>
      </ul>

      <h3>Focus in Smart Filters</h3>
      <ul>
        <li>When clearing (exiting) a filter, the focus is set back to what it was before using the filter (even from a previous session).</li>
        <li>Increased the size of the Focus button in the filter editor. [Mac]</li>
        <li>Fixed the focus button showing up when focused via a filter. [iPad & iPhone]</li>
        <li>When adding a new Smart Filter, if currently Focused, that focus is saved in the filter.</li>
        <li>When using a saved filter, any focus state is restored.</li>
        <li>When clearing (exiting) the filter, the focus state remains; it can be cleared separately if desired, but will often still be wanted.</li>
        <li>When changing or clearing the focus, the filter is cleared, since its conditions are no longer satisfied.</li>
        <li>Added a "From focus" button in the filter editor, that shows the filtered focus, if any.</li>
        <li>Clicking/tapping that button shows a popover listing the topics of the top-level rows, and the disclosures can be expanded to show any levels. Checkboxes are included to select zero, one, or more topics for focus.</li>
        <li>If a parent topic is selected, its children can't be selected, since they are included by the parent.</li>
        <li>The disclosure state in the picker is remembered, except that the selected topics are automatically disclosed.</li>
        <li>When selecting a filter that contains a focus, or choosing a Focus in the Outline view, the sidebar is expanded to show the selected focus.</li>
      </ul>

      <h3>Date picker improvements</h3>
      <ul>
        <li>In the Outline view, the date picker menu button is now available in parent cells with calculations.</li>
        <li>The date picker menu button now doesn't unexpectedly disappear after choosing a date.</li>
        <li>The date picker now includes a value pop-up menu at the top, available for parent rows, enabling choosing between custom values or Latest or Earliest calculations.</li>
        <li>The date picker now has a bit more space, to fit with macOS 26. [Mac]</li>
        <li>Fixed the date picker not working properly when accessed via the Timeline Info popover.</li>
      </ul>

      <h3>Added handy arrow key shortcuts</h3>
      <ul>
        <li>Pressing Option-Left Arrow in the Outline view when not editing a cell will collapse the selected row(s); if the row can't be collapsed, its parent (if any) is collapsed and selected.</li>
        <li>Pressing Option-Right Arrow will correspondingly expand the selected row(s), if possible.</li>
        <li>Moved the Show/Hide Sidebar menu command into a Sidebar submenu.</li>
        <li>Added Select Previous Item and Select Next Item commands to that submenu, with keyboard shortcuts Cmd-Option-Up Arrow and Cmd-Option-Down Arrow, to enable navigating the sidebar via the keyboard.</li>
        <li>Also added Collapse Item and Expand Item to the Sidebar submenu, with shortcuts Cmd-Option-Left Arrow and Cmd-Option-Right Arrow, to collapse or expand the selected item in the sidebar.</li>
        <li>On iPad, the sidebar shortcuts require the Control key too, as iPadOS doesn't allow Cmd-Option arrows. [iPad]</li>
      </ul>

      <h3>Template Chooser improvements</h3>
      <ul>
        <li>Reimplemented the Template Chooser to use a native split view sidebar style on macOS 26. [Mac]</li>
        <li>Added a Recents sidebar section in the Template Chooser to quickly view recently used templates. [Mac]</li>
        <li>Updated the Open Recent drop-down menu to use the new one (see below). [Mac]</li>
        <li>Fixed the Project Plan template so the Successors column remains hidden when first opened on another device.</li>
      </ul>

      <h3>Enhanced recent documents</h3>
      <ul>
        <li>Replaced File > Open Recent with a custom menu grouped by date ranges, with tooltips showing the location. [Mac]</li>
        <li>It is more resilient to temporary cloud outages (won't forget recents if briefly offline). [Mac]</li>
        <li>If a recent document can’t be resolved, you can choose to locate it or remove it from Recents. [Mac]</li>
      </ul>

      <h3>Added About window</h3>
      <ul>
        <li>Added an About SheetPlanner window, available from the More menu and app menu. [iPad & iPhone]</li>
      </ul>

      <h3>New Settings</h3>
      <ul>
        <li>Added an option on the General page for the Spacebar action: by default pressing the spacebar with a cell selected in the Outline (when not editing) will start editing, but this option enables you to instead toggle the completed state. [Mac]</li>
        <li>Pressing Option-Space will reverse this behavior. [Mac]</li>
        <li>Added options on the Defaults page for the font and color to use for new notes. [Mac]</li>
      </ul>

      <h3>Live sync fixes and improvements</h3>
      <ul>
        <li>Reduced live sync participant start/stop notification churn by delaying stop notifications briefly and canceling them if activity resumes.</li>
        <li>When a document is an observer, the Save command is disabled. [Mac]</li>
        <li>Fixed a bug where syncing didn't start for a newly saved document, leading to the publisher state being inaccurate.</li>
        <li>Improved the publisher evaluation logic.</li>
        <li>Fixed changes not being saved to the document after being synced from an observer.</li>
        <li>Significant performance boost and faster ready-to-sync by remembering more sync state (the boost will apply after it does one more full sync with the cloud).</li>
        <li>Several more fixes and improvements to fast syncing.</li>
      </ul>

      <h3>Fast sync now supports Dropbox, Google Drive, OneDrive, and other cloud providers</h3>
      <ul>
        <li>Now detects when a document is saved on a cloud drive other than iCloud Drive.</li>
        <li>If so, and the document is open on more than one device at the same time, changes are only saved to the cloud drive from one device, to prevent conflicts.</li>
        <li>The first device to open the document will typically save, but that could change depending on usage.</li>
        <li>The changes from each device will be synchronized via the fast-sync mechanism, so all devices have the same content.</li>
        <li>If another device is publishing changes, the window subtitle will say "Shared" instead of "Edited".</li>
      </ul>

      <h3>Sync diagnostics logging</h3>
      <ul>
        <li>Added an optional sync diagnostics logging pane in the Activity Inspector.</li>
        <li>Only available as a Debug menu option; enable the Debug menu (via Settings), then enable Diagnostic Logging, then choose Sync Diagnostics ▸ Show in Activity to make the pane show up.</li>
        <li>Includes the current publisher status and sync status.</li>
        <li>Also includes an auto-updating log of publisher- and sync-related log entries (there are a lot!).</li>
        <li>Plus a Copy button, that copies all log entries onto the clipboard.</li>
        <li>Tip: if using Copy on an iPad, you can paste the text into a TextEdit document on your Mac thanks to Universal Clipboard.</li>
        <li>Useful to help trace publisher and sync issues.</li>
      </ul>

      <h3>Performance improvements</h3>
      <ul>
        <li>Reduced redundant split-view state updates to improve view refresh responsiveness. [Mac]</li>
        <li>Reduced unnecessary divider and split-resize bookkeeping work when switching views to improve tab-switch responsiveness. [Mac]</li>
        <li>Reduced unnecessary full Outline data reloads during view switching to improve Outline tab responsiveness. [Mac]</li>
        <li>Reduced repeated hidden-Outline split sizing and unnecessary Inspector reloads during view updates to cut tab-switch lag spikes. [Mac]</li>
        <li>Added diagnostics for frequent Inspector reload bursts to help identify remaining reload churn. [Mac]</li>
        <li>Reduced Inspector refresh overhead by avoiding duplicate scroll restoration and redundant print-container layout updates. [Mac]</li>
        <li>Reduced hidden-Inspector split bookkeeping work and added finer Inspector reload diagnostics to identify remaining spikes. [Mac]</li>
        <li>Stopped reloading folders on simple view switches unless folder/sidebar state changed. [Mac]</li>
        <li>Coalesced nested Inspector reload requests and improved Inspector timing diagnostics to isolate remaining reload spikes. [Mac]</li>
        <li>Optimized Inspector disclosure updates to avoid unnecessary expand/collapse work during view switches. [Mac]</li>
        <li>Skipped redundant Inspector disclosure and scroll restoration work when state is unchanged. [Mac]</li>
        <li>Reduced false Inspector disclosure updates caused by group reordering during reloads. [Mac]</li>
        <li>Reduced Outline redraw overhead by skipping header reload work during redraw-only refreshes and avoiding unchanged grid color updates. [Mac]</li>
        <li>Avoided redundant Inspector print-container updates when print mode is unchanged. [Mac]</li>
        <li>Reduced unnecessary Outline topic-width recalculation when split positions are unchanged. [Mac]</li>
        <li>Avoided unnecessary base split-divider positioning while sidebar or Inspector panes are hidden. [Mac]</li>
        <li>Skipped redundant post-layout view-bounds updates when split state and bounds are unchanged. [Mac]</li>
        <li>Fixed some significant CPU usage, e.g. trying to update the Timeline when it wasn't shown.</li>
        <li>Made logging much more efficient (important since it is used extensively).</li>
        <li>The Diagnostic Log now updates every 10 seconds in the background, and starts a new log for each launch or day.</li>
        <li>Disabled some excessive logging.</li>
        <li>When resizing the window or columns, the note, links, and tags now remain visually unchanged (but behind the scenes temporarily replaced with images, for performance reasons). [Mac]</li>
        <li>Fixed an issue with the sidebar.</li>
        <li>Plus other improvements.</li>
      </ul>

      <h3>Apple Intelligence (experimental)</h3>
      <ul>
        <li>Added an Apple Intelligence child-row generation experiment. [Mac]</li>
        <li>To enable it, show the Debug menu and turn on Enable Intelligence (only shown when Apple Intelligence is available on this Mac). [Mac]</li>
        <li>Then use Organize ▸ Generate Child Rows on a selected row to generate and add child rows from the row's Topic and Note. [Mac]</li>
        <li>Use the note to write a prompt to guide the rows to generate and columns to populate; the more detail you provide, the more likely to be what you want. [Mac]</li>
        <li>If the selected row already has child rows, you'll be prompted to Replace, Append, or Cancel before rows are added. [Mac]</li>
        <li>Generated rows always include Topic and can also fill other columns (for example Start Date, Finish Date, and People) when sensible. [Mac]</li>
      </ul>

      <h3>Plus many other changes</h3>
      <ul>
        <li>Rebuilt Mac app menus in code for easier maintenance and aligned more shared menu commands between Mac and iPad.</li>
        <li>Added icons for all app menus.</li>
        <li>Fixed the initial visibility of the sidebar and Inspector so they are now restored from the saved state.</li>
        <li>Added a Debug menu option to temporarily disable optimization paths for A/B performance testing.</li>
        <li>Fixed the note text color; now properly adapts between light and dark modes.</li>
        <li>Fixed a crash when loading a document with a lot of sheets. [iPad]</li>
        <li>Fixed layout of the Settings view in landscape on iPhone. [iPhone]</li>
        <li>Fixed slow layout on phone rotation after showing the Inspector. [iPhone]</li>
        <li>The Month and Year views now update correctly when selecting a row in the sidebar.</li>
        <li>Fixed a crash when attempting to drag a row onto one of its own children.</li>
        <li>Replaced the toolbar search field with a dynamic search toolbar item: it resizes with the window, and if the window is narrow, it collapses into a button, and expands when clicked, or on Cmd-F. Plus implemented pressing Esc to clear and exit the search field. [Mac]</li>
        <li>Fixed the sidebar completion pies updating when changing between light and dark modes.</li>
        <li>Fixed Focus in New Tab. [Mac]</li>
        <li>Moved updating records to after loading is complete, to avoid accessing settings not yet loaded.</li>
        <li>Fixed sometimes unexpectedly reverting the active theme when syncing.</li>
        <li>When hiding the Inspector, and there is empty space to the right of the last column in the Outline, the Topic column width is now adjusted to eliminate that empty space.</li>
      </ul>
      <p>
        Note: changes apply to the Mac, iPad, and iPhone editions, unless otherwise noted.
      </p>




      <hr />

      <h2>SheetPlanner 5.1 Release Notes</h2>
      <time>October 24th, 2025</time>
      <p>
        SheetPlanner 5.1 is a big update to this spreadsheet-like project planning tool.
        Major enhancements in version 5.1 include:
      </p>
      <ul>
        <li>Added a new Tags default column.</li>
        <li>Added Smart Filter options to include children with matches and to restore views.</li>
        <li>The Focus feature now supports multiple selections.</li>
        <li>The sheets in the sidebar can now disclose their contents to quickly see the structure and focus on different parts of the document.</li>
        <li>Added special handling for importing CSV exported from OmniFocus.</li>
        <li>Plus many other improvements; see the full release notes on the SheetPlanner website for details.</li>
      </ul>
      <p>
        Read on for a detailed list of changes.
      </p>

      <h3>Tags</h3>
      <ul>
        <li>Added a new Tags default column. Hidden by default; reveal via a column menu.</li>
        <li>Includes some sample tags: Home, Work, Errands, Ideas, Projects. You can add or remove as desired.</li>
        <li>Added an "Automatically add tags" option in the Format Inspector for Tags columns. It is on by default for the Tags column. This option is also available for other list-based columns but is off by default for them.</li>
        <li>The Tags column uses a new capsule display style, with different colors assigned to each tag.</li>
        <li>Type some text and press Comma (<code>,</code>) or Return to turn it into a tag, either matching one of the existing ones or as a custom one. If the auto-add option is enabled, custom ones are added to the Tags column.</li>
      </ul>

      <h3>Smart Filters</h3>
      <ul>
        <li>Added an option in the filter editor to choose whether to show only the matching rows (the old and default behavior) or to also include children of matches.</li>
        <li>Added an option to restore the current views when showing the filter.</li>
        <li>Smart Filters for List columns now include <em>"contains"</em>, <em>"doesn't contain"</em>, <em>"begins with"</em>, and other text conditions.</li>
        <li>Can now add # before some text in the search field to only search for tags.</li>
        <li>Adding a Smart Filter for #-prefixed search text adds the filter for tags, e.g. <em>"#work"</em> is added as <code>Tags Contains "work"</code>.</li>
      </ul>

      <h3>Multi-focus</h3>
      <ul>
        <li>The Focus feature now supports multiple selections.</li>
        <li>Select as many rows as desired and choose the Focus command, and they and their children will all be focused upon.</li>
        <li>If the selection includes parents and children, the uppermost parents are used.</li>
        <li>Right-click (on Mac) or long-press (on iPad) on the Focus button to the right of the Topic heading to show a menu to re-focus on any of the parents, or unfocus.</li>
      </ul>

      <h3>Sidebar focus</h3>
      <ul>
        <li>The sheets in the sidebar now have a disclosure.</li>
        <li>When the disclosure is expanded, they list the Completion checkbox and Topics of top-level rows of the sheet.</li>
        <li>Each subsequent level can also be expanded; the disclosure state is remembered.</li>
        <li>Selecting a row shows the containing sheet with that row focused.</li>
        <li>Multiple selection (for multi-focus) is also supported via Cmd-clicking or tapping rows.</li>
        <li>On iPad and iPhone, can also swipe with two-fingers for multiple selection.</li>
        <li>When a sheet is expanded, selecting the sheet exits focus mode, as a convenient shortcut.</li>
        <li>Rows can be dragged within the current sheet in the sidebar, and from the sidebar to the Outline view or vice versa.</li>
      </ul>

      <h3>OmniFocus import</h3>
      <ul>
        <li>Added special handling for importing CSV exported from OmniFocus.</li>
        <li>Populates the built-in Topic, Start Date, Finish Date, Completion Date, Duration, and Tags columns, plus custom Status, Context, and Flag columns, and some hidden columns with additional values that aren't really needed.</li>
        <li>In OmniFocus, show the rows to export (e.g., via a Perspective), then choose <code>File ▸ Export…</code>, and choose <code>Comma Separated Values (CSV)</code> for the File Format. Then in SheetPlanner, choose <code>File ▸ Import…</code> and choose that file.</li>
      </ul>

      <h3>iPad & iPhone specific</h3>
      <ul>
        <li>Added a Filter menu to the menubar on iPad, with the same commands and shortcuts as on Mac.</li>
        <li>The sidebar now restores the selection on opening the document.</li>
        <li>The sidebar is now wider by default, and the maximum width is wider.</li>
        <li>The width of the sidebar can be adjusted and remembered for next time.</li>
        <li>Fixed the focus button occasionally missing its title.</li>
        <li>Fixed documents sometimes not saving.</li>
      </ul>

      <h3>Other changes</h3>
      <ul>
        <li>Significant changes to support safer concurrency with Swift 6.2.</li>
        <li>Fixed a row number appearing as zero in rare circumstances.</li>
        <li>Indenting and outdenting are now allowed while focused.</li>
        <li>Fixed a crasher when changing Settings after a document is closed.</li>
        <li>Fixed sometimes losing the completion state when dragging a row.</li>
        <li>When there is no selection, pressing Down Arrow will select the first Topic cell, or pressing Up Arrow will select the last one.</li>
        <li>Fixed an issue with Cmd-tapping/clicking to select multiple cells in the Outline view.</li>
        <li>Fixed dragging Outline view rows when completed rows are hidden.</li>
      </ul>
      
      <hr />

      <h2>SheetPlanner 5.0 Release Notes</h2>
      <time>August 26th, 2025</time>
      <p>

        SheetPlanner 5.0 is a major update to this spreadsheet-like project planning tool. Major enhancements in version 5 include:
      </p>
      <ul>
        <li>Introducing an iPad and iPhone app for SheetPlanner!</li>
        <li>Added a powerful live sync feature, where changes update across devices almost instantaneously.</li>
        <li>Split the Inspector into Values, Format, Views, and Activity tabs.</li>
        <li>Plus many other improvements; see the full release notes on the SheetPlanner website for details.</li>
      </ul>

      <p>
        Read on for a detailed list of changes.
      </p>

      <h3>iPad and iPhone apps</h3>
      <ul>
        <li>Introducing an app designed for iPad and iPhone, optimized for those devices.</li>
        <li>They support all view modes of the Mac app: Outline, Timeline, Board, Month, and Year.</li>
        <li>Plus the full range of Inspector controls and multi-sheet sidebar.</li>
        <li>And advanced features like focus, filters, notes, links, and more.</li>
        <li>Documents are synced via iCloud Drive, including the new live sync feature.</li>
      </ul>

      <h3>Live sync</h3>
      <ul>
        <li>Documents can be synced via iCloud Drive, and now will update pretty much in real time.</li>
        <li>Individual changes are communicated between devices faster than iCloud Drive syncs the whole document.</li>
        <li>(For the technically minded, it uses CloudKit, like Apple's Numbers etc do.)</li>
        <li>Live sync works with collaboration too; share a SheetPlanner document with someone else, and you'll see changes quickly.</li>
        <li>It can optionally notify you when someone or another device starts or stops using the document, or makes changes.</li>
      </ul>

      <h3>Outline</h3>
      <ul>
        <li>Major changes to improve performance of the Outline view. Things like selecting cells and updating other cells based on edits is significantly faster.</li>
        <li>The Outline is now updated less frequently if a cell is being edited or a popover is shown (e.g. date picker) when a sync occurs.</li>
        <li>Dragging rows is disallowed when using a Smart Filter.</li>
        <li>Deleting all children of a parent then undoing that will now re-expand the parent.</li>
        <li>When deleting the last child of a parent, the entire outline is now reloaded to remove the parent disclosure.</li>
        <li>When clearing a Progress cell, the row is now marked as incomplete, removing the Topic strikethrough etc.</li>
        <li>The Organize ▸ Disconnect menu command now works when one or both of the selected rows is a parent row.</li>
      </ul>

      <h3>Timeline</h3>
      <ul>
        <li>Reimplemented on Mac to support the iPad and iPhone app.</li>
        <li>Improved the reliability of drawing the dividing lines and such.</li>
        <li>Now avoids a situation where it was unnecessarily updating the Timeline when not shown.</li>
        <li>Fixed the headers not updating when changing the scale.</li>
        <li>Multiple selection is supported via the Command key, and extending a selection via the Shift key.</li>
        <li>The Project Plan templates now default to a Timeline scale of Weeks.</li>
      </ul>

      <h3>Board</h3>
      <ul>
        <li>Now defaults to Topic for vertical lanes.</li>
      </ul>

      <h3>Inspector</h3>
      <ul>
        <li>Added Values, Format, Views, and Activity tabs to the Inspector, and split the panes amongst them.</li>
        <li>Moved the View ▸ Show/Hide Inspector menu command into a submenu, along with commands to directly show each of the Inspector tabs.</li>
        <li>Remembers the selected tab for next time.</li>
        <li><h4>Note Inspector:</h4>
          <ul>
            <li>Added a Note Inspector pane when a cell is selected, with a rich text field like in the Topic cell.</li>
            <li>Changes in the Note Inspector are reflected in the Topic cell, and vice versa.</li>
          </ul>
        </li>
        <li><h4>Links Inspector:</h4>
          <ul>
            <li>Added a Links Inspector pane when a cell is selected, with a list of links from the Topic cell.</li>
            <li>Includes buttons with the links; clicking or tapping on a button shows a menu with actions, just like in the Topic cell.</li>
            <li>Changes in the Links Inspector are reflected in the Topic cell, and vice versa.</li>
            <li>Links can be reordered by dragging.</li>
            <li>Below the links are buttons to add a new file link or website link.</li>
          </ul>
        </li>
        <li><h4>Interval Inspector:</h4>
          <ul>
            <li>Added a Units popup menu with Automatic, Hours, Days, Weeks, Months, and Years. By default it is Automatic.</li>
            <li>Added a checkbox to use work days and hours. Defaults off.</li>
            <li>Added a segmented control with days of the week, to indicate which days are work days. Uses the system setting for the first day of the week. Defaults to Monday to Friday; disabled if not using work days.</li>
            <li>Added time fields for the start and finish work hours. Defaults to 9 AM to 5 PM; disabled if not using work hours. Only relevant when using Hours as units.</li>
          </ul>
        </li>
        <li><h4>Symbol Column Inspector:</h4>
          <ul>
            <li>Added a Goal Symbol option.</li>
          </ul>
        </li>
        <li><h4>People Column Inspector:</h4>
          <ul>
            <li>The People Column inspector now lists devices for people using the document. You can rename your current device.</li>
          </ul>
        </li>
        <li><h4>Format Style Inspector:</h4>
          <ul>
            <li>The Style Inspector's font family menu is now filtered to remove some non-English fonts when the locale is English, to make the menu a bit more manageable.</li>
            <li>Added an "Include All Font Families" option in the Debug menu to bypass this behavior (on next launch of the app).</li>
            <li>Added a Capitalization popup menu in the Style Inspector, with options to change the case of typed text.</li>
          </ul>
        </li>
        <li><h4>Activity Inspector:</h4>
          <ul>
            <li>This new pane lists dates and people who have changed the document.</li>
            <li>A sync status icon button is included before to the Activity group name. Clicking/tapping this button will manually sync. A tooltip gives more information on the state of syncing.</li>
            <li>When the Activity Inspector page is displayed, all change highlights are shown. Selecting an activity row dims the other highlights. When changing to another Inspector page or dismissing the Inspector, the highlights revert to the global setting (i.e. are removed, unless always showing all highlights).</li>
          </ul>
        </li>
      </ul>

      <h3>Filters</h3>
      <ul>
        <li>Changed the criteria editor to retain the same condition and value when changing the column if the column type is the same.</li>
      </ul>

      <h3>Sidebar</h3>
      <ul>
        <li>The All Sheets and Recents groups in the sidebar are now collapsed by default for new documents.</li>
        <li>Right-clicking on the sheet name now just shows the contextual menu, without going into edit mode.</li>
        <li>Fixed getting stuck with the spinning indicator when opening a document if a folder was selected.</li>
        <li>The New Folder, New Sheet…, New Sheet from Document…, Rename Folder and Delete Folder menu items are now available when a folder is selected.</li>
      </ul>

      <h3>Settings</h3>
      <ul>
        <li>Added a Sync page to Settings, with options for activity notifications, selections, and highlights.</li>
        <li>Changed the toolbar icons to use modern SF Symbols.</li>
        <li>Added a "Show Debug menu" checkbox in General Settings, to show or hide the Debug menu.</li>
      </ul>

      <h3>OS 26 compatibility</h3>
      <ul>
        <li>Built for macOS 26, still compatible with several previous macOS versions.</li>
      </ul>

      <h3>Other changes</h3>
      <ul>
        <li>Updated the welcome (onboarding) images.</li>
        <li>Changed the minimum macOS version required to Ventura (macOS 13).</li>
        <li>Plus many more minor changes and fixes.</li>
      </ul>

      <hr />
      
      <h2>SheetPlanner 4.0</h2>
      <time>September 2nd, 2023</time>
      
      <p>
        SheetPlanner 4.0 is a major update to this spreadsheet-like project planning tool. Major enhancements in version 4 include:
        <ul>
          <li>SheetPlanner now requires macOS Monterey (12.0) or later.</li>
          <li>Added a new kanban-style Board view to arrange lanes of cards.</li>
          <li>Completion and Progress are now linked.</li>
          <li>List cells now display customizable colors for each list item.</li>
          <li>Move cells around the Outline view via keyboard shortcuts.</li>
          <li>Plus many other improvements; see the full release notes on the SheetPlanner website for details.</li>
        </ul>
      </p>

      <p>
        Read on for a detailed list of changes.
      </p>

      <h3>Improved free trial</h3>
      <ul>
        <li>No longer displays the unlock options on first launch.</li>
        <li>Now automatically starts the trial on first launch, so all features can be tried for free.</li>
        <li>The trial now only counts days the app is open, so taking a break from the trial won't use up trial days.</li>
      </ul>

      <h3>Board view is a paid upgrade</h3>
      <ul>
        <li>Version 4 includes a powerful new <strong>Board</strong> view. To use it, a paid upgrade is required, other than for recent purchasers.</li>
        <li>All other current features remain available for previous purchasers.</li>
        <li>The upgrade trial only counts days on which the Board view are displayed.</li>
        <li>The Upgrade bar appears only while the Board view is displayed, to emphasize that the upgrade is related to that view.</li>
        <li>The Upgrade window appears at the end of the trial, with options to just keep using the already unlocked features, or buy an upgrade.</li>
        <li>Attempting to show the Board view after the trial has ended will instead show the Upgrade window.</li>
        <li>The Unlock/Upgrade window now uses dynamic text, so it's easier to change the wording.</li>
      </ul>

      <h3>Board view</h3>
      <ul>
        <li>Added a new kanban-style Board view to arrange lanes of cards.</li>
        <li>Supported column content and types include Completed, Topic, List, and Symbol.</li>
        <li>Supports vertical and horizontal lanes, which can be collapsed or expanded.</li>
        <li>Clicking a selected card shows an Info popover; the popover can be detached by dragging its arrow.</li>
        <li>Can Command-click cards to select multiple cards, or drag a selection from empty space.</li>
        <li>Can drag cards between vertical and/or horizontal lanes to move them.</li>
        <li>Cards show a menu button when hovering over them.</li>
        <li>This menu includes the options from the View / Board menu to change the lanes used for the Board view.</li>
        <li>It also includes a command to Collapse/Expand Cards, which removes or adds the values after the topic.</li>
        <li>When expanded, the menu also includes a command to Show/Hide Value Names, which shows or hides name labels above each value.</li>
        <li>The menu also includes the available card values, like in the Board Inspector, to quickly show or hide values.</li>
        <li>All these options are also available in the View / Board menu, and the Board Inspector.</li>
        <li>When the Outline view is hidden, pressing arrow keys will select cards based on their positions on the board.</li>
        <li>Arrow selection also supports Cmd-arrows to select multiple cards. (Shift-arrows is equivalent.)</li>
        <li>Pressing the Space bar when a single card is selected will show the info popover. (Press Escape to dismiss.)</li>
      </ul>

      <h3>Board Inspector</h3>
      <ul>
        <li>Added a Board section to the Inspector, included when the Board view is displayed.</li>
        <li>Includes menus to choose the vertical and horizontal lanes, like in the View / Board menu.</li>
        <li>Includes a list of checkboxes to choose which values to include in the cards; they can be dragged to change the order.</li>
      </ul>

      <h3>Completion and Progress are now linked</h3>
      <ul>
        <li>When a row is marked as complete, the Progress column is set to 100%.</li>
        <li>When a row is marked as incomplete, the Progress column is set back to what it was before 100%.</li>
        <li>When the Progress is set to 100%, the row is marked as complete.</li>
        <li>When the Progress is set to less than 100%, the row is marked as incomplete.</li>
        <li>The Completion pie now reflects the Progress value in the Outline and Board views.</li>
        <li>Existing documents are updated to reflect this change.</li>
      </ul>

      <h3>List colors</h3>
      <ul>
        <li>The List Inspector now includes color pickers for each list item.</li>
        <li>List cells now display the color of their value.</li>
        <li>The list picker menu now also shows the colors.</li>
        <li>The People list picker also shows the icons and colors.</li>
      </ul>

      <h3>Move submenu</h3>
      <ul>
        <li>Added a Move submenu to the Organize menu, with items to move selected rows up, down, left, or right.</li>
        <li>The rows are only moved within their parents.</li>
        <li>Multiple rows can be moved at once (including from different parents).</li>
        <li>Undo is supported.</li>
      </ul>

      <h3>Menus and toolbar changes</h3>
      <ul>
        <li>Rearranged the View menu, to put the views and their options into common submenus.</li>
        <li>When the focused row is selected, the Focus toolbar button now changes to <em>Unfocus</em>, as an alternative to the circled x icon in the Topic header.</li>
        <li>Fixed the Hide Column item in the column menus not being enabled.</li>
        <li>Fixed the Delete Column item in the column menu not being enabled for custom columns.</li>
        <li>Added a SheetPlanner Release Notes item to the Help menu, to show the full release notes on the website.</li>
      </ul>

      <h3>Templates and opening documents</h3>
      <ul>
        <li>Added templates for Sprint Planning Board and Software Development.</li>
        <li>Updated the To Do List template.</li>
        <li>When saving a new custom template, it now automatically shows the rename popover, to make it easier to give it a better name.</li>
        <li>Added an Open Recent drop-down menu to the Template Picker, as an alternative to the File / Open Recent menu.</li>
        <li>Fixed a crash opening a document on some machines.</li>
        <li>Improved file caching to avoid conflicts when editing two documents with the same name.</li>
      </ul>

      <h3>Sharing</h3>
      <ul>
        <li>Changed the Sharing toolbar icon to the macOS standard icon.</li>
        <li>The Share toolbar button now uses the sharing popover introduced in macOS Ventura.</li>
        <li>When choosing File / Share..., the sharing panel now appears as a popover from the Share toolbar button.</li>
        <li>Moved the Show/Hide Highlights menu command to the View menu, and renamed Show/Hide Change Highlights to make the function more clear.</li>
      </ul>

      <h3>Sidebar</h3>
      <ul>
        <li>Fixed the sidebar limiting resizing in some situations.</li>
        <li>Double-clicking the sidebar divider will change it to a default width (proportional to the window size).</li>
        <li>Right-clicking on the sheet name when not already editing it will now display the sidebar contextual menu.</li>
        <li>Fixed duplicate sheets in the sidebar.</li>
        <li>Fixed missing sheet if not edited.</li>
        <li>Fixed contextual menu in sidebar.</li>
        <li>Fixed renaming a sheet.</li>
        <li>Fixed incorrect document handling behavior when importing to a new document.</li>
      </ul>

      <h3>Outline view changes</h3>
      <ul>
        <li>The Outline's Progress cell now treats a blank value as zero, so it always shows the bar, pie, or percentage.</li>
        <li>Fixed the contextual menu when right-clicking on a cell.</li>
        <li>Fixed themes in the Outline view.</li>
        <li>Replaced the Progress cell bar with a custom view to avoid excessive blinking of the standard view.</li>
        <li>The Outline now automatically discloses rows when needed for jumping to predecessors or successors, or for Board navigation.</li>
        <li>Updated all of the color pickers to use the macOS native control instead of a buggy third-party one, now that macOS supports a nicer control with a color palette popover.</li>
        <li>List columns now use Summary when they become a parent (unless they have a custom value assigned; calculations don't override custom values).</li>
        <li>When indenting rows, the connections are now adjusted to bypass the new parent row, since parent rows can't have connections.</li>
        <li>When a row is indented or outdented, or moved left or right, the row height is now adjusted.</li>
        <li>Fixed cells not editing after their column was unhidden or initially added.</li>
        <li>Fixed the Progress and Completion Date cells ending ending while typing; they now wait a few second before updating (being linked).</li>
        <li>Fixed autocomplete when typing in a People cell.</li>
        <li>Fixed assigning calculations to cell values when they become a parent via indenting or dragging.</li>
        <li>Fixed disconnecting a dependency for parent rows.</li>
        <li>Pressing the Escape key will now unfocus if focused, cancel filtering if filtering, or exit the print preview if that's shown.</li>
        <li>Fixed undoing unfocusing.</li>
      </ul>

      <h3>Timeline view changes</h3>
      <ul>
        <li>Removed the extraneous disclosure triangles in the Timeline view.</li>
        <li>When printing the Timeline, it now defaults to no empty columns before the first populated one (this can be changed in the Print Inspector).</li>
        <li>Fixed a rare issue with Timeline child rows not appearing.</li>
        <li>Fixed missing horizontal and vertical grid lines in the Timeline view.</li>
      </ul>

      <h3>Calendar views</h3>
      <ul>
        <li>The Month and Year views now show only the matching items when focusing or filtering.</li>
        <li>Improved the scrolling behavior when unfocusing or canceling filtering.</li>
        <li>The Topic Column Inspector's Completed style (Hidden, Checkbox, or Pie) is now used in the Month view.</li>
      </ul>

      <h3>Inspector changes</h3>
      <ul>
        <li>Rows can now be selected in the List and People Inspectors, to make it more intuitive that they can be reordered and deleted.</li>
        <li>Fixed adding rows in the List and People Inspectors, so they are selected and editing when added.</li>
        <li>Added a tooltip to the Date Column Inspector color to explain that this color used in the Month view.</li>
      </ul>

      <h3>Other changes</h3>
      <ul>
        <li>Fixed filters using Any Column when evaluating the People column.</li>
        <li>Changed the logic of searching/filtering to be consistent between using Any Column or a specific column.</li>
        <li>Vertically centered the date picker button in the date cells.</li>
        <li>Fixed an error for some people with the newsletter signup form.</li>
        <li>Added a control in the Settings to enable overriding the system appearance to always use light or dark mode.</li>
        <li>Updated the onboarding screens and What's New for version 4.</li>
        <li>SheetPlanner now requires macOS Monterey (12.0) or later.</li>
      </ul>
      
      {/*-----------------------------*/}
      <hr />
      {/*-----------------------------*/}
      
      <h2>SheetPlanner 3.0</h2>
      <time>May 25th, 2022</time>

      <h3>Important notes</h3>
      <ul>
        <li><span role="img" aria-label={"warning"}>⚠️</span> The document format changed in version 3.0, so version 2.0.6 and earlier won't be able to open documents opened in version 3.0.</li>
        <li><span role="img" aria-label={"warning"}>🖥</span> SheetPlanner now requires macOS Catalina (10.15) or later.</li>
      </ul>

      <h3>Multiple sheets and tabs</h3>
      <ul>
        <li>Everyone can now use multiple sheets and tabs in a document.</li>
        <li>A sidebar is available to organize multiple sheets.</li>
        <li>Sheets, Notes, and Links can be displayed in a new tab.</li>
        <li>These were previously only available to paid users; now also available in free mode.</li>
      </ul>

      <h3>Sync</h3>
      <ul>
        <li>Saving to iCloud Drive is now supported. New documents are saved in iCloud Drive by default, but can be saved elsewhere if desired.</li>
        <li>When saved to iCloud Drive, sharing options are available. See Share ▸ Share File menu, and the Share toolbar button.</li>
        <li>Detects when a document is changed on two or more computers at once, and merges the changes, to avoid one overwriting the other's.</li>
        <li>Now uses Quick Look preview and thumbnail app extensions to dynamically provide those to the Finder, to avoid conflicts when editing a document on multiple Macs.</li>
        <li>Quick Look previews can be zoomed and scrolled, though are static snapshots of the window content.</li>
        <li>Added support for Mac-specific bookmarks for file links, so finding a file on one Mac won't invalidate the link on another Mac.</li>
        <li>The showing state of notes and links is now also Mac-specific.</li>
        <li>Changed the way the outline hierarchy is restored when loading a document, to minimize changes needed to be saved, thus reducing conflicts when syncing.</li>
      </ul>
      
      <h3>Collaboration selection</h3>
      <ul>
        <li>When another Mac makes changes to a document, the changed cells are briefly highlighted.</li>
        <li>Added a command to Show/Hide Highlights. When shown, all cells that have been edited are highlighted with the person's color. When hidden, cells are only highlighted when changed remotely.</li>
        <li>When two or more people are editing the same document at the same time, the other people's selections are displayed as thin lines, using their highlight colors.</li>
      </ul>

      <h3>People column</h3>
      <ul>
        <li>Added a standard People column, hidden by default, that is a List-type column pre-populated with the names of people collaborating on a document.</li>
        <li>When the People column is displayed, it works the same as other List columns: click the menu button to show a menu of people, or type a name. The cell also displays the selected person's color; this color is used to highlight their changes.</li>
        <li>Can add and remove custom people via the Inspector.</li>
        <li>Collaborators and custom people use different icons in the People column.</li>
        <li>In the Column Inspector, the color assigned to each person can be changed.</li>
        <li>Also in the Inspector, the names can be edited, e.g. to use a shorter name.</li>
        <li>The Inspector also includes an option to choose multiple people, or only one.</li>
        <li>When one or more people have been assigned to a row via the People column, and a notification is set for a date column, now only those people are notified.</li>
        <li>Updated the Project Plan and Sales Pipeline templates to use the People column instead of a custom List column.</li>
      </ul>

      <h3>Cell Inspector</h3>
      <ul>
        <li>When a cell that has been changed is selected, the Cell Inspector now displays information about it, including:
          <ul>
            <li>The date and time the cell was last changed.</li>
            <li>The highlight color and name of the person who made the change.</li>
            <li>The name of the computer where the change was made.</li>
          </ul>
        </li>
      </ul>
      
      <h3>Smart filters</h3>
      <ul>
        <li>Added the ability to filter on Row attributes, like whether a row is a task (has a duration) or milestone (no duration), is a parent (has sub-rows) or a child (no rows within), is unchanged, was changed by you, or was changed by someone else, etc.</li>
        <li>Fixed the Note filtering to properly support searching within notes, from start of notes, etc.</li>
        <li>Smart filters now support filtering on the People column.</li>
      </ul>
      
      <h3>More compact documents</h3>
      <ul>
        <li>The SheetPlanner document is now a single file, instead of a package (a folder of files presented as one file).</li>
        <li>This makes syncing faster and more reliable, in addition to taking up less space on disk, among other benefits.</li>
        <li>Note that documents opened in version 3.0 and later won't be able to be opened in version 2.0.6 or earlier.</li>
        <li>Documents saved by SheetPlanner 3 use the file extension ".sp".</li>
      </ul>
      
      <h3>Debug</h3>
      <ul>
        <li>Added a command in the Debug menu to open the diagnostic log file, that records information to help debug the app (it is replaced each day).</li>
        <li>Also added a command to open the Console, which logs that and other Apple framework detritus.</li>
        <li>Added Debug menu options to add the computer name or app edition name to the person name. Useful to test the People column without multiple people.</li>
      </ul>
      
      <h3>Other changes</h3>
      <ul>
        <li>On macOS 11 and later, the window now has the title above the toolbar, instead of to the left, so toolbar buttons are spaced out instead of scrunched to the right.</li>
        <li>When a cell is selected, the color picker is now updated to reflect the text color of that cell.</li>
        <li>The Inspector no longer scrolls to the top when changing the cell selection etc.</li>
        <li>When a document is saved, its print settings are saved as the default.</li>
        <li>When a template is loaded, the default print settings are loaded.</li>
        <li>When opening a template, the sheet name now defaults to the template name.</li>
        <li>When adding a sheet to an existing document, the sheet name has a number appended to make it unique, if necessary.</li>
        <li>As a special case, adding a blank sheet uses the name "Sheet", with a number if needed.</li>
        <li>Fixed row numbers being zeroes when using Duplicate Sheet & Rows.</li>
        <li>Fixed a curly issue where a cell could unexpectedly stop editing.</li>
        <li>When copying a Topic cell with notes and/or links, those are now also included when pasting into a document.</li>
        <li>When opening a document on a different computer, the first sheet is selected by default, instead of none.</li>
        <li>The Month and Year view headers now better adapt between light and dark modes.</li>
        <li>Fixed Month and Year views not updating when in a different tab (not a sync-related issue).</li>
        <li>Updated the Welcome to SheetPlanner screens.</li>
      </ul>


      {/*-----------------------------*/}
      <hr />
      {/*-----------------------------*/}
      
      <h2 id="b10">SheetPlanner 2.0</h2>
      <time>Dec 29, 2020</time>
      <h3>Big Sur compatibility</h3>
      <ul>
        <li>Updated the app icon and sidebar icons for the new Big Sur style.</li>
        <li>Updated for Big Sur (macOS 11) compatibility, but still supports back to High Sierra (macOS 10.13).</li>
        <li>SheetPlanner is now a universal app for Intel and Apple M1 silicon.</li>
      </ul>

      <h3>Free mode</h3>
      <ul>
        <li>The Outline view can now be used free of charge, including all outlining features, and saving documents.</li>
        <li>When in free outliner mode, the toolbar and menus are simplified to omit paid features, including the Sidebar, Timeline, Month, and Year views, smart filters, and multiple tabs.</li>
        <li>In free mode, if opening a document that contains multiple sheets, they appear as multiple unclosable tabs.</li>
        <li>The purchase bar at the top of the window now only appears during a trial of the paid features.</li>
      </ul>

      <h3>Welcome window</h3>
      <ul>
        <li>When first running SheetPlanner, a window will appear with a slideshow introducing the features of the app.  This can be re-displayed later via the Help ▸ Welcome to SheetPlanner menu command.</li>
        <li>When updating to a new build of the app, a window will appear with "What's New in SheetPlanner".  This can be re-displayed later via the Help ▸ What's New in SheetPlanner menu command.</li>
        <li>When the app hasn't been purchased, a window will be displayed with options to use the free outliner, start a trial, unlock the full features, restore a purchase, or learn more.</li>
        <li>Even if the trial has been completed in version 1, it will be available to try again in version 2.</li>
        <li>After the unlock page, a newsletter signup page will be displayed.  This can be re-displayed via Help ▸ Sign Up for the SheetPlanner Newsletter.</li>
      </ul>

      <h3>Help & Tutorials window</h3>
      <ul>
        <li>Added a new Help & Tutorials window.</li>
        <li>This is displayed via the Help ▸ SheetPlanner Help menu command.</li>
        <li>Includes a sidebar that lists the help topics and video tutorials.</li>
        <li>Clicking a help section displays the help for that topic.</li>
        <li>Clicking a video displays the corresponding video tutorial.</li>
      </ul>

      <h3>Multiple sheets in a document</h3>
      <ul>
        <li>Added a sidebar to the left of the Outline view in the document window, including built-in and custom folders and sheets.</li>
        <li>A document can now include multiple sheets, each with their own columns, rows, styles, filters, etc.</li>
        <li>Built-in folders include all sheets, recently accessed sheets, favorite sheets, and deleted sheets.</li>
        <li>Each tab or window of a document can have a different sheet selected, and each with their own shown views and selections.</li>
        <li>Added items to the Add toolbar menu to add new folders and sheets.</li>
        <li>Added a new Sheet menu to the menubar, with commands to add new folders and sheets, duplicate, rename, delete, and navigate between sheets.</li>
        <li>Can click on a selected folder or sheet to rename it.</li>
        <li>Can click the star icon on a selected sheet to mark it as a favorite.</li>
        <li>Folders and sheets can be dragged to rearrange them.</li>
        <li>The view state is stored for each sheet of each tab of each computer.</li>
        <li>The selected sheet name is displayed in the window/tab title.</li>
        <li>This is only available in the trial and purchased modes.</li>
      </ul>

      <h3>Document changes for multiple sheets and future sync features</h3>
      <ul>
        <li>Major changes to the document format to support the big new feature of multiple sheets in a document.</li>
        <li>Support for organizing sheets into nestable folders.</li>
        <li>Support for multiple people and/or Macs editing a document via cloud syncing (coming in a future version), each with their own tabs, sheet and cell selection, etc.</li>
        <li>Numerous changes to improve the performance of document access.</li>
        <li>Plus other changes for the latest version of Swift.</li>
        <li><span role={"img"} aria-label={"caution"}>🚧</span> NOTE: the document format has changed, so documents opened by this version onwards can't be opened in version 1.2 and earlier.</li>
      </ul>

      <h3>My Templates</h3>
      <ul>
        <li>Added a File ▸ Save as Template… command to save the current document as a custom template, that you can use as a basis when making new documents.</li>
        <li>Custom templates are listed in a new "My Templates" section of the Template Chooser (which is only included if some have been saved).</li>
        <li>Right-click on a custom template in the chooser for menu commands to rename or delete it.</li>
        <li>Improved the Template Chooser to work better from the keyboard: you can now just arrow around to select a template, then press the Return key to open it.</li>
        <li>Updated the appearance of the Template Chooser window.</li>
      </ul>

      <h3>Caboodle import</h3>
      <ul>
        <li>Added support for importing documents from Caboodle, maintaining the hierarchy and content of the entries.</li>
        <li>Custom fields are added as columns, and entry text are added as notes.</li>
        <li>Supports both version 1 (from Dejal) and version 2 (from Before Dawn) of Caboodle.</li>
        <li>If any encrypted entries are encountered, you will be asked for a password, or can skip them, or stop the import.</li>
        <li>If the same password is used for multiple entries, you only need to enter it once; it'll only ask again for unique passwords.</li>
      </ul>

      <h3>Style simplification</h3>
      <ul>
        <li>Changed the style scope popup menu to rename "All at This Level" to "Level n Rows", where "n" is the selected row number.</li>
        <li>Removed the Parent/Child option, since that caused confusion.</li>
        <li>When the scope is changed to "Level n Rows", the level style is applied to the selected row.</li>
        <li>Existing documents are updated accordingly.</li>
      </ul>

      <h3>Pasting improvements</h3>
      <ul>
        <li>If only one cell is selected when pasting text with multiple lines and/or columns, it will now overwrite (and select) adjacent rows and/or columns.</li>
        <li>If more columns than are available are needed, new ones are added at the end.</li>
        <li>If more rows than are available within the parent of the selected cell, more are added at the end of that parent.</li>
        <li>If the selected cell is the last within its parent, added rows can reflect the hierarchy of the pasted text.  The hierarchy is ignored if overwriting adjacent cells, to avoid changing existing hierarchy.</li>
        <li>If multiple cells are selected, pasting only populates those cells, as before.</li>
        <li>Copying and pasting now uses the displayed column order; previously the order could be different if columns have been moved.</li>
      </ul>

      <h3>Print preview</h3>
      <ul>
        <li>Choosing the Print… command now transforms the window into a print preview.</li>
        <li>When in the print preview, a Print… button in the Inspector and the Print… menu command displays the standard print sheet to actually print.</li>
        <li>When in the print preview, a Done button in the Inspector and a File ▸ Done Printing menu command enable exiting the print preview.</li>
        <li>The print preview always uses the light appearance, even when the Mac is using the dark appearance, to match the paper.  Defaults to Plain theme, but that can be changed.</li>
        <li>The Outline and/or Timeline views are displayed so that all of their columns are visible.  The Month and Year views can not be printed yet (coming in a later version).</li>
        <li>Outline and Timeline views are fully editable in print preview mode.  Cells can be edited, rows can be collapsed or expanded, filters and focus can be used, etc.</li>
        <li>The preview can be zoomed in or out via a pinch gesture on a trackpad.</li>
        <li>The Inspector includes a Print pane with pop-up menus to choose the printer and paper size.</li>
        <li>The Print Inspector also includes graphical representations of the page to toggle between portrait and landscape, plus top, bottom, left, and right margins.</li>
        <li>The Inspector includes checkboxes to include the row numbers and/or column headings when printing.</li>
        <li>The views can be scaled via a slider or percentage field in the Inspector, so they fit a desired number of pages, with a Fit button to fit their width to one page.</li>
        <li>Page breaks are indicated with dotted lines; they are aligned to the gaps between cells.</li>
      </ul>

      <h3>Preferences</h3>
      <ul>
        <li>The Preferences window has been split into two pages, with the usual page-switching toolbar buttons.</li>
        <li>The General page includes behavior options like whether to use a template, the Return and Tab key options, etc.</li>
        <li>The Appearance page includes cosmetic options to apply as defaults in new sheets, like the theme and font, plus new options for the completion and topic style, finish date options, timeline options, and calendar options.</li>
        <li>The default text color now uses an enhanced color well, with the pop-up swatches.</li>
      </ul>

      <h3>Tweaks and fixes</h3>
      <ul>
        <li>The focus path and dismiss button are now displayed in the Topic column header instead of a separate bar.</li>
        <li>Changed the Show Completed Rows option to support three options: Show Completed, Show Completed Today, and Hide Completed.  The new today option leaves just completed rows visible until the following day, so recently completed rows can be referred to if needed.</li>
        <li>Added a pop-up menu to the Import open panel to choose the destination of the import: a new document, or a new sheet on the topmost open document.</li>
        <li>If holding Option when clicking a view in the toolbar, that view replaces the current view(s) instead of adding another one.</li>
        <li>Can now sort (via the Organize ▸ Sort Once submenu) by the Added Date to get back to the order rows were added, or by the Changed Date to see the recently edited rows.</li>
        <li>Changed the Filter menu to have the filters at the end, instead of the start, to match the Sheet menu (and other apps).</li>
        <li>Disabled the limit of making the Topic column narrower than the total custom columns, so blank space can be left to the right of the last column if desired (I can easily re-enable it if we want to go back to the old behavior).</li>
        <li>Re-sampled each of the color palette colors to match those in Apple Numbers.</li>
        <li>Changing between light and dark modes now refreshes the Inspector, so it shows the correct color.</li>
        <li>The label for links now uses a color with more contrast.  Also increased the text length before truncation, and fixed a layout issue with multiple long links.</li>
        <li>When showing or hiding the filter bar, other tabs are now updated to match that state.</li>
        <li>When hiding the note or links, now exits editing mode.</li>
        <li>The tooltip for the Connect toolbar button changes between Connect Dependency and Disconnect Dependency as appropriate.</li>
        <li>Fixed updating the dates when loading the Expense Tracker template.</li>
        <li>Replaced the icon for the Completed options in the toolbar with a tri-state one.</li>
        <li>Fixed parent rows with some completed children not appearing when showing Completed Today.</li>
        <li>Fixed changing the completed options displaying rows as collapsed when they shouldn't be.</li>
        <li>Fixed updating the Timeline and calendar views when changing the focus.</li>
        <li>Clicking a cell next to a link now selects the cell.</li>
        <li>Fixed showing notes and links in a new tab.</li>
        <li>Adjusted the dark variation of the Yellow Notepad theme.</li>
        <li>Fixed row menu items with submenus not applying their items to the clicked row.</li>
        <li>When opening a new document or sheet from a template, the Added and Changed dates are set to the current date and time, with a 1-second offset for each row, so sorting by Added Date will restore the templated order.</li>
        <li>If the dates for the Timeline are more than ten years ago, only those dates are displayed, rather than including today in the range.</li>
        <li>Added support for a minimum Timeline scale of years.</li>
        <li>The Timeline tooltip now only includes dates that have their "show in timeline" Column Inspector option checked.</li>
        <li>Fixed the undo for a Clear Value command saying Undo Delete instead of Undo Clear Cell.</li>
        <li>No longer beeps when reaching the beginning or end while arrowing between cells.</li>
        <li>Changed the name picker (e.g. for saving a smart filter) to include buttons to cancel and save the change, as alternatives to pressing the Escape or Return keys.</li>
        <li>Changed the date picker to not automatically dismiss when clicking on a part that doesn't change the date (e.g. the month name, or dates in previous or next months).</li>
        <li>When multiple rows are selected, the Edit ▸ Duplicate Row menu command becomes Duplicate Selected Rows, and similar for the row menu.</li>
        <li>If the preference for new documents is set to a fixed template, but one hasn't been explicitly chosen, now defaults to Blank (which is the first in the menu anyway).</li>
        <li>Adding rows, indenting, and outdenting are now disabled when using a filter, since they don't make sense in that context.</li>
        <li>Fixed the filter criterion value popup not always appearing for Symbol columns.</li>
        <li>Improved filter criteria on Symbol columns to work properly with unchecked symbols.</li>
        <li>The Show Note in New Tab menu command is now always visible in the row menu. (It appears in the View menu when you hold the Shift key.)</li>
        <li>Fixed a template opening in a new tab of an existing document.</li>
        <li>Fixed a crash when dragging a row into its own children; that shouldn't be and no longer is allowed.</li>
        <li>When editing a date cell via the date picker, other cells are now immediately updated. (Editing by typing in the cell updates when leaving the cell, to avoid unnecessary updates for incomplete dates.)</li>
        <li>Fixed using the date picker in the Inspector vs the Timeline or calendar info popover.</li>
        <li>Now shows the Show/Hide Completed toolbar button menu when clicking instead of clicking and releasing, for quicker access.</li>
        <li>The Connect toolbar button now shows the connected/disconnected state in its icon (with an arrow when connected, none when not). It also shows it immediately on click, rather than after completion the operation, to appear more responsive.</li>
        <li>Fixed a crash on High Sierra and Mojave when accessing the Window menu.</li>
        <li>Reimplemented the Shift-selection behavior to create rectangular selections, anchored from the initial click point (so can be reshaped with each Shift-click or Shift-arrow), in the same way as Numbers etc.</li>
        <li>Updated the row selection functions (clicking the row number to select the row, the Select row commands, etc) to select the whole row and support rectangular selection adjustments.</li>
        <li>Fixed undoing the Timeline Shade Weekends option in the Inspector.</li>
        <li>Fixed undoing dragging the Timeline bars.</li>
        <li>Improved linking to a website when the URL has spaces around it, by trimming off those spaces.</li>
        <li>When dragging files to a document to link to them, now only highlights the row under the drag.</li>
      </ul>

      <hr />

      <h2>SheetPlanner 1.2.1</h2>
      <time>Feb 4, 2020</time>
      <ul>
        <li>Fixed an issue where dragging parents and children could lose their hierarchy.</li>
        <li>When copying selected cells, the text lines for child rows are now prepended with spaces to preserve the hierarchy.</li>
        <li>When pasting text with a single cell selected, or while editing, when adding rows it now looks for space prefixes to preserve the hierarchy.</li>
        <li>Fixed the Show All Notes and Show All Links not toggling to their Hide variations.</li>
        <li>Fixed inability to change the Style Inspector from All at This Level to Parent/Children at Level.</li>
        <li>Fixed the text color defaulting to black on dark mode for some new users.</li>
        <li>The Preferences window now updates the text color when changing between light and dark modes.</li>
      </ul>

      <hr />

      <h2>SheetPlanner 1.2</h2>
      <time>Dec 13, 2019</time>
      <ul>
        <li>Added a new Picture column type, that can include pictures within the document.</li>
        <li>Pictures can be dragged or pasted, shown as thumbnails, and have a larger Quick Look view.</li>
        <li>Can now optionally choose multiple items in List cells.</li>
        <li>Each cell can now have its own style, including font, face, size, text style, justification, and color.</li>
        <li>Replaced the Style Inspector font button with separate pop-up menus, and added an alignment control.</li>
        <li>Can now add a notification for Date cells, a specified interval before the date.</li>
        <li>The Timeline view now supports an editable info popover when double-clicking on a bar.</li>
        <li>Added a Date Stamp function.</li>
        <li>Plus numerous other improvements; see the website for details.</li>
      </ul>

      <hr />

      <h2>SheetPlanner 1.1.1</h2>
      <time>Jul 27, 2019</time>
      <ul>
        <li>Changing a Date column color, or toggling the show checkboxes, is now immediately reflected in the Timeline view.</li>
        <li>Fixed the Outline Columns submenu showing incorrect column names after adding or removing columns.</li>
        <li>The Progress column is now shown in the Project Plan template.</li>
        <li>If you open a template then close it without making any changes, it now doesn't ask if you want to keep it.</li>
      </ul>

      <hr />

      <h2>SheetPlanner 1.1</h2>
      <time>Jun 25, 2019</time>
      <ul>
        <li>Added the ability to connect rows to establish a dependency.</li>
        <li>Dependencies are displayed via arrows in the Timeline, and new optional columns in the Outline.</li>
        <li>A red vertical line now marks the current date & time in the Timeline.</li>
        <li>Any Date column can now be included in the Timeline.</li>
        <li>New Symbol column type, including checkbox, flag, star, color icons, bullet journaling, Eisenhower quadrants, 5 stars, etc.</li>
        <li>New Progress column type, displayed as a progress bar, mini pie chart, or percentage.</li>
        <li>Auto-enter cell values with a default value, from the row above, today, formatted serial number, and more.</li>
        <li>Outline view menu improvements.</li>
        <li>Filter improvements, including better search for dates, time intervals, note text, and more.</li>
        <li>Printing the Timeline is now supported, and printing the Outline is improved.</li>
        <li>Plus numerous other improvements; see the website for details.</li>
      </ul>

      {/*-----------------------------*/}
      <hr />
      {/*-----------------------------*/}

      <h2>SheetPlanner 1.0.1</h2>
      <time>Jan 4, 2019</time>
      <ul>
        <li>Fixed a layout issue with the calendar views when always showing scrollbars is enabled in the System Preferences.</li>
        <li>Fixed issues with editing Number column values in some Euro countries.</li>
        <li>Fixed a crash that could occur when changing Preferences after closing a document.</li>
        <li>Fixed an issue where a document could not be opened.</li>
      </ul>

      
      <Link to="/"><span className={"link"}>Go back to the homepage</span></Link>
    </Content>
  </Layout>
)

export default ReleaseNotesPage
